import { fetchWithFallback } from "../OrgUsageAnalytics/api";

// The AI half of this page is LIVE. It rides the same Langfuse-backed
// observability endpoint the AI Usage Analytics page uses, but asks it
// different questions: we want every trace name (not the four presets), and we
// lean on `groupBy` + `secondaryGroupBy` to get two-dimensional breakdowns
// (feature x model, feature x client) in a single round trip.
//
// Everything OUTSIDE the AI section — GCP infra, comms, CRM, payments — has no
// billing API wired up yet, so it comes from the curated catalog in `data.js`.
// See MISSING_INTEGRATIONS there for what would make those live too.

const ANALYTICS_PATH = "/observability/langfuse-analytics";
const ORG_NAMES_PATH = "/observability/organization-names";

/**
 * `rowLimit` is capped at 500 server-side. A two-dimensional breakdown burns
 * that budget fast (features x clients), so callers that only need the top
 * slice should pass something smaller and stay well inside the cap.
 */
const MAX_ROW_LIMIT = 500;

const buildAnalyticsParams = ({
  window = "24h",
  from,
  to,
  groupBy,
  secondaryGroupBy,
  traceName,
  organizationId,
  model,
  includeAllTraceNames = true,
  rowLimit = 200,
} = {}) => {
  const params = {
    timeWindow: window,
    rowLimit: Math.min(rowLimit, MAX_ROW_LIMIT),
  };
  // `includeAllTraceNames` is what stops the backend from silently narrowing to
  // its four DEFAULT_ANALYTICS_TRACE_NAMES presets. A cost view that only
  // counted four features would understate spend without saying so.
  if (includeAllTraceNames) params.includeAllTraceNames = "true";
  if (from) params.fromTimestamp = from;
  if (to) params.toTimestamp = to;
  if (groupBy) params.groupBy = groupBy;
  if (secondaryGroupBy) params.secondaryGroupBy = secondaryGroupBy;
  if (traceName) params.traceName = traceName;
  if (organizationId) params.organizationId = organizationId;
  if (model) params.model = model;
  return params;
};

/** One environment's worth of AI cost, shaped however `groupBy` asks for. */
export const fetchAiAnalytics = async (env, options = {}) =>
  fetchWithFallback(ANALYTICS_PATH, buildAnalyticsParams(options), {
    env,
    timeoutMs: 90000,
  });

/**
 * Prod and dev in parallel, because the whole point of the env split here is
 * seeing them side by side — the original audit found dev quietly burning
 * production-grade tokens, which is invisible if you can only view one at a
 * time. A failure in one environment must not blank out the other, so each
 * settles independently and reports its own error.
 */
export const fetchAiAnalyticsBothEnvs = async (options = {}) => {
  const [prod, dev] = await Promise.allSettled([
    fetchAiAnalytics("prod", options),
    fetchAiAnalytics("dev", options),
  ]);

  const unwrap = (settled) =>
    settled.status === "fulfilled"
      ? { data: settled.value, error: null }
      : { data: null, error: settled.reason?.message || String(settled.reason) };

  return { prod: unwrap(prod), dev: unwrap(dev) };
};

/**
 * Resolve org IDs to names, 200 per call (the server's cap). Returns a plain
 * id -> name map; ids that don't resolve are simply absent, and callers should
 * fall back to showing the raw id rather than hiding the row — an
 * unresolvable org still costs money.
 */
export const fetchOrganizationNames = async (env, ids = []) => {
  const unique = Array.from(new Set(ids.filter(Boolean).map(String)));
  if (unique.length === 0) return {};

  const chunks = [];
  for (let i = 0; i < unique.length; i += 200) {
    chunks.push(unique.slice(i, i + 200));
  }

  const results = await Promise.allSettled(
    chunks.map((chunk) =>
      fetchWithFallback(ORG_NAMES_PATH, { ids: chunk.join(",") }, { env }),
    ),
  );

  return results.reduce((acc, settled) => {
    if (settled.status === "fulfilled" && settled.value?.names) {
      Object.assign(acc, settled.value.names);
    }
    return acc;
  }, {});
};
